// PostGallery.js - Complete implementation with carousel support
import React, { useCallback, useState } from 'react';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faVideo, 
  faMusic, 
  faImages, 
  faImage,
  faPlay,
  faLink,
  faEdit,
  faTrash,
  faFlag,
  faShare,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import 'photoswipe/style.css';
import styles from './PostGallery.module.css';

const PostGallery = ({ 
  posts, 
  isOwner, 
  onEdit, 
  onDelete, 
  onReport,
  onShare,
  onGalleryOpen,
  isMobile = false 
}) => {
  // Track current carousel index for each post
  const [carouselIndices, setCarouselIndices] = useState({});

  const renderPostPreview = useCallback((post) => {
    // Handle embedded posts
    if (post.isEmbed) {
      return (
        <div className={styles.postPreview}>
          <img 
            src={post.thumbnailUrl || '/embed-placeholder.png'} 
            alt={post.caption || 'Embedded content'}
            className={styles.previewImage}
          />
          <div className={styles.typeIndicator}>
            <FontAwesomeIcon icon={faLink} />
          </div>
        </div>
      );
    }

    switch (post.type) {
      case 'video':
        return (
          <div className={styles.postPreview}>
            <img 
              src={post.thumbnailUrl || post.url} 
              alt={post.caption || 'Video thumbnail'}
              className={styles.previewImage}
            />
            <div className={styles.playOverlay}>
              <FontAwesomeIcon icon={faPlay} className={styles.playIcon} />
            </div>
            <div className={styles.typeIndicator}>
              <FontAwesomeIcon icon={faVideo} />
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className={styles.postPreview}>
            <img 
              src={post.thumbnailUrl || '/default-audio-cover.png'} 
              alt={post.caption || 'Audio cover'}
              className={styles.previewImage}
            />
            <div className={styles.typeIndicator}>
              <FontAwesomeIcon icon={faMusic} />
            </div>
          </div>
        );

      case 'photo':
        // For carousel posts, show the first image with indicator
        if (post.isCarousel && post.carousel && post.carousel.length > 0) {
          const currentIndex = carouselIndices[post.postId] || 0;
          const currentItem = post.carousel[currentIndex];
          
          return (
            <div className={styles.postPreview}>
              <img 
                src={currentItem.thumbnailUrl || currentItem.url} 
                alt={post.caption || 'Carousel photo'}
                className={styles.previewImage}
              />
              <div className={styles.typeIndicator}>
                <FontAwesomeIcon icon={faImages} />
              </div>
            </div>
          );
        }
        
        // Regular photo
        return (
          <div className={styles.postPreview}>
            <img 
              src={post.thumbnailUrl || post.url} 
              alt={post.caption || 'Photo'}
              className={styles.previewImage}
            />
          </div>
        );

      default:
        return (
          <div className={styles.postPreview}>
            <img 
              src={post.thumbnailUrl || '/default-thumbnail.png'} 
              alt={post.caption || 'Post'}
              className={styles.previewImage}
            />
          </div>
        );
    }
  }, [carouselIndices]);

  // Handle action clicks - edit, delete, report, share
  const handleActionClick = useCallback((action, post, pswp) => {
    switch (action) {
      case 'edit':
        onEdit?.(post);
        pswp.close();
        break;
      case 'delete':
        if (onDelete) {
          onDelete(post.postId);
          pswp.close();
        }
        break;
      case 'report':
        onReport?.(post);
        pswp.close();
        break;
      case 'share':
        // Don't close gallery when sharing
        onShare?.(post);
        break;
    }
  }, [onEdit, onDelete, onReport, onShare]);

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Define custom UI elements for PhotoSwipe
  const uiElements = [
    {
      name: 'custom-caption',
      order: 9,
      isButton: false,
      appendTo: 'bar',
      onInit: (element, pswp) => {
        let captionHTML = '';
        
        pswp.on('change', () => {
          const currentPost = posts[pswp.currIndex];
          
          captionHTML = `
            <div class="pswp-caption-content">
              <div class="pswp-user-info">
                <img src="${currentPost.profilePictureUrl || '/default-avatar.png'}" class="pswp-avatar" />
                <span class="pswp-username">${currentPost.userName || 'User'}</span>
              </div>
              <p class="pswp-caption-text">${currentPost.caption || ''}</p>
              ${currentPost.tags?.length ? `
                <div class="pswp-tags">
                  ${currentPost.tags.map(tag => `<span class="pswp-tag">#${tag}</span>`).join('')}
                </div>
              ` : ''}
              <p class="pswp-date">${formatDate(currentPost.createdAt)}</p>
              <div class="pswp-actions">
                ${isOwner ? `
                  <button class="pswp-action-btn pswp-edit-btn" title="Edit">
                    <span class="pswp-icon">✎</span> Edit
                  </button>
                  <button class="pswp-action-btn pswp-delete-btn" title="Delete">
                    <span class="pswp-icon">🗑</span> Delete
                  </button>
                ` : `
                  <button class="pswp-action-btn pswp-report-btn" title="Report">
                    <span class="pswp-icon">⚠</span> Report
                  </button>
                `}
                <button class="pswp-action-btn pswp-share-btn" title="Share">
                  <span class="pswp-icon">↗</span> Share
                </button>
              </div>
            </div>
          `;
          
          element.innerHTML = captionHTML;
        });

        // Add specific click handlers to the buttons
        element.addEventListener('click', (e) => {
          const currentPost = posts[pswp.currIndex];
          
          if (e.target.closest('.pswp-edit-btn')) {
            handleActionClick('edit', currentPost, pswp);
          } else if (e.target.closest('.pswp-delete-btn')) {
            handleActionClick('delete', currentPost, pswp);
          } else if (e.target.closest('.pswp-report-btn')) {
            handleActionClick('report', currentPost, pswp);
          } else if (e.target.closest('.pswp-share-btn')) {
            handleActionClick('share', currentPost, pswp);
          }
        });
      }
    },
    
    // Add navigation controls for carousel posts
    {
      name: 'carousel-nav',
      order: 9,
      isButton: false,
      appendTo: 'wrapper',
      onInit: (element, pswp) => {
        // Create navigation buttons HTML
        element.innerHTML = `
          <button class="pswp-carousel-nav pswp-carousel-prev">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
          <button class="pswp-carousel-nav pswp-carousel-next">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
          <div class="pswp-carousel-indicators"></div>
        `;
        
        const prevBtn = element.querySelector('.pswp-carousel-prev');
        const nextBtn = element.querySelector('.pswp-carousel-next');
        const indicators = element.querySelector('.pswp-carousel-indicators');
        
        // Handle slide change to update carousel controls visibility
        pswp.on('change', () => {
          const currentPost = posts[pswp.currIndex];
          
          // Hide carousel controls for non-carousel posts
          if (!currentPost.isCarousel || !currentPost.carousel || currentPost.carousel.length <= 1) {
            element.style.display = 'none';
            return;
          }
          
          // Show carousel controls and update indicators
          element.style.display = 'block';
          
          // Update carousel indicators
          let indicatorsHTML = '';
          currentPost.carousel.forEach((_, index) => {
            indicatorsHTML += `
              <button class="pswp-carousel-indicator ${index === 0 ? 'active' : ''}" data-index="${index}"></button>
            `;
          });
          indicators.innerHTML = indicatorsHTML;
          
          // Update active indicator
          const currentSubIndex = 0; // Default to first image in carousel
          const indicatorBtns = indicators.querySelectorAll('.pswp-carousel-indicator');
          indicatorBtns.forEach((btn, index) => {
            btn.classList.toggle('active', index === currentSubIndex);
          });
        });
        
        // Handle carousel navigation
        prevBtn.addEventListener('click', () => {
          const currentPost = posts[pswp.currIndex];
          if (!currentPost.isCarousel || !currentPost.carousel) return;
          
          const currentSubIndex = 0; // Get current sub-index
          const newSubIndex = (currentSubIndex === 0) ? 
            currentPost.carousel.length - 1 : currentSubIndex - 1;
          
          // Update carousel image - would require custom implementation
          console.log('Navigate to previous carousel image:', newSubIndex);
        });
        
        nextBtn.addEventListener('click', () => {
          const currentPost = posts[pswp.currIndex];
          if (!currentPost.isCarousel || !currentPost.carousel) return;
          
          const currentSubIndex = 0; // Get current sub-index
          const newSubIndex = (currentSubIndex === currentPost.carousel.length - 1) ? 
            0 : currentSubIndex + 1;
          
          // Update carousel image - would require custom implementation
          console.log('Navigate to next carousel image:', newSubIndex);
        });
        
        // Handle indicator clicks
        indicators.addEventListener('click', (e) => {
          const indicator = e.target.closest('.pswp-carousel-indicator');
          if (!indicator) return;
          
          const index = parseInt(indicator.dataset.index);
          if (isNaN(index)) return;
          
          // Update carousel image - would require custom implementation
          console.log('Navigate to carousel image at index:', index);
        });
      }
    }
  ];

  // Gallery options
  const galleryOptions = {
    showHideAnimationType: 'fade',
    bgOpacity: 0.9,
    padding: { top: 60, bottom: 60, left: 60, right: 60 },
    loop: true,
    pinchToClose: true,
    closeOnVerticalDrag: true,
    spacing: 0.1,
    zoomAnimationDuration: 300,
    hideAnimationDuration: 200,
    showAnimationDuration: 200,
    easing: 'cubic-bezier(.4,0,.22,1)',
  };

  // For carousel posts, we need to create individual items for each image
  const flattenedPostsForGallery = [];
  const postIndexMap = {};
  
  posts.forEach((post, index) => {
    // Track the original post index
    postIndexMap[flattenedPostsForGallery.length] = index;
    
    // If it's a carousel, add each image as a separate item
    if (post.isCarousel && post.carousel && post.carousel.length > 0) {
      post.carousel.forEach((carouselItem, carouselIndex) => {
        // First carousel item is the main one
        if (carouselIndex === 0) {
          flattenedPostsForGallery.push({
            ...post,
            url: carouselItem.url,
            thumbnailUrl: carouselItem.thumbnailUrl || carouselItem.url,
            contentType: carouselItem.contentType,
            isFirstCarouselItem: true,
            carouselIndex: carouselIndex,
            totalCarouselItems: post.carousel.length
          });
        } else {
          // Add additional carousel items with reference to the original post
          flattenedPostsForGallery.push({
            ...post,
            url: carouselItem.url,
            thumbnailUrl: carouselItem.thumbnailUrl || carouselItem.url,
            contentType: carouselItem.contentType,
            isFirstCarouselItem: false, 
            carouselIndex: carouselIndex,
            totalCarouselItems: post.carousel.length
          });
        }
      });
    } else {
      // Regular post, just add it as is
      flattenedPostsForGallery.push(post);
    }
  });

  // Create data source for PhotoSwipe
  const createDataSource = () => {
    return flattenedPostsForGallery.map((post) => {
      // For embedded content, use html
      if (post.isEmbed && post.embedHtml) {
        return {
          html: `
            <div class="pswp-full-media">
              <div class="pswp-embed-wrapper">
                ${post.embedHtml}
              </div>
            </div>
          `,
          width: 1200,
          height: 800,
          msrc: post.thumbnailUrl || '/embed-placeholder.png',
          // Store original post data for reference
          originalPost: post,
          carouselInfo: post.isCarousel ? {
            index: post.carouselIndex,
            total: post.totalCarouselItems
          } : null
        };
      }
      
      // Handle by post type
      switch (post.type) {
        case 'video':
          return {
            html: `
              <div class="pswp-full-media">
                <video 
                  controls 
                  controlsList="nodownload"
                  poster="${post.thumbnailUrl || ''}"
                  class="pswp-media"
                  id="media-${post.postId}"
                >
                  <source src="${post.url}" type="${post.contentType || 'video/mp4'}" />
                  Your browser does not support the video tag.
                </video>
              </div>
            `,
            width: 1200,
            height: 800,
            msrc: post.thumbnailUrl || post.url,
            originalPost: post,
            carouselInfo: post.isCarousel ? {
              index: post.carouselIndex,
              total: post.totalCarouselItems
            } : null
          };
          
        case 'audio':
          if (post.isEmbed && post.embedHtml) {
            return {
              html: `
                <div class="pswp-full-media">
                  <div class="pswp-embed-wrapper">
                    ${post.embedHtml}
                  </div>
                </div>
              `,
              width: 1200,
              height: 800,
              msrc: post.thumbnailUrl || '/default-audio-cover.png',
              originalPost: post,
              carouselInfo: post.isCarousel ? {
                index: post.carouselIndex,
                total: post.totalCarouselItems
              } : null
            };
          } else {
            return {
              html: `
                <div class="pswp-full-media">
                  <div class="pswp-audio-wrapper">
                    ${post.thumbnailUrl ? `<img src="${post.thumbnailUrl}" class="pswp-audio-cover" alt="Audio cover" />` : ''}
                    <audio controls class="pswp-audio-player" id="media-${post.postId}">
                      <source src="${post.url}" type="${post.contentType || 'audio/mpeg'}" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              `,
              width: 1200,
              height: 800,
              msrc: post.thumbnailUrl || '/default-audio-cover.png',
              originalPost: post,
              carouselInfo: post.isCarousel ? {
                index: post.carouselIndex,
                total: post.totalCarouselItems
              } : null
            };
          }
          
        case 'photo':
        default:
          return {
            html: `
              <div class="pswp-full-media">
                <img 
                  src="${post.url}" 
                  alt="${post.caption || 'Post image'}"
                  class="pswp-media"
                />
                ${post.isCarousel ? `
                  <div class="pswp-carousel-indicator-overlay">
                    <span class="pswp-carousel-count">${post.carouselIndex + 1} / ${post.totalCarouselItems}</span>
                  </div>
                ` : ''}
              </div>
            `,
            width: 1200,
            height: 800,
            msrc: post.thumbnailUrl || post.url,
            originalPost: post,
            carouselInfo: post.isCarousel ? {
              index: post.carouselIndex,
              total: post.totalCarouselItems
            } : null
          };
      }
    });
  };

  // Update gallery options with the data source
  const updatedGalleryOptions = {
    ...galleryOptions,
    dataSource: createDataSource(),
  };

  return (
    <Gallery 
      uiElements={uiElements} 
      options={updatedGalleryOptions}
      onOpen={onGalleryOpen}
    >
      <div className={styles.postsGrid}>
        {posts.map((post, index) => {
          if (post.isCarousel && post.carousel && post.carousel.length > 0) {
            // Only show the first image of the carousel in the grid
            return (
              <Item
                key={post.postId}
                original={post.carousel[0].url}
                thumbnail={post.carousel[0].thumbnailUrl || post.carousel[0].url}
                width={1200}
                height={800}
                index={index} // This is important to navigate to the correct index
              >
                {({ ref, open }) => (
                  <div
                    ref={ref}
                    onClick={open}
                    className={styles.gridItem}
                  >
                    {renderPostPreview({...post, url: post.carousel[0].url, thumbnailUrl: post.carousel[0].thumbnailUrl})}
                  </div>
                )}
              </Item>
            );
          } else {
            // Regular non-carousel post
            return (
              <Item
                key={post.postId}
                original={post.url}
                thumbnail={post.thumbnailUrl || post.url}
                width={1200}
                height={800}
                index={index}
              >
                {({ ref, open }) => (
                  <div
                    ref={ref}
                    onClick={open}
                    className={styles.gridItem}
                  >
                    {renderPostPreview(post)}
                  </div>
                )}
              </Item>
            );
          }
        })}
      </div>
    </Gallery>
  );
};

export default PostGallery;
